import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import Select from "react-select";

import { useFormik } from "formik";
import * as Yup from "yup";

import { Referrer } from "models/referrer/referrer";
import LoadingComponent from "common/components/loader";

import TableContainer from "common/table-container";
import { RoutePath } from "routes/route-path";
import { CreateReferrer, UpdateReferrer, GetReferrers } from "../../api/referrer";
import { AppUtility } from "utility/app-utility";

const validationSchema = Yup.object({
  name: Yup.string().required("Customer name is required"),
  contactNumber: Yup.string().required("Phone is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  perecentage: AppUtility.formikCurrencyValidtion(),
});

interface Props {
  referrer?: Referrer;
  onReferrerSaveSuccess?: (referrer: Referrer) => void;
}

const AddEditReferrerComponent: React.FC<Props> = ({
  referrer: referrer,
  onReferrerSaveSuccess: onReferrerSaveSuccess,
}) => {
  const navigate = useNavigate();
  // Retrieve query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const optParam = searchParams.get("opt");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedReferrer, setSelectedReferrer] = useState<Referrer | undefined>();
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {}, []);

  useEffect(() => {
    console.log(referrer);
    if (referrer) {
      setSelectedReferrer({ ...referrer });
      setEditMode(true);
      formik.setValues(referrer); // Set formik values when tenant prop changes
    }
  }, [referrer]);

  const initialValues = {
    name: selectedReferrer?.name ?? "",
    contactNumber: selectedReferrer?.contactNumber ?? "",
    email: selectedReferrer?.email ?? "",
    isActive: !selectedReferrer?.id ? true : selectedReferrer?.isActive ?? false,
    code: selectedReferrer?.code ?? "",
    nic: selectedReferrer?.nic ?? "",
    percentage: selectedReferrer?.percentage ?? 0,
  };
  //TODO:open from existing vehicle from url param
  const formik: any = useFormik({
    initialValues: initialValues,
    validationSchema,
    onSubmit: (values: any) => {
      // console.log(values);
      addEditReferrer(values);
    },
  });

  const handleResetClick = () => {
    setEditMode(false);
    setSelectedReferrer({ ...initialValues, isActive: true });
    formik.resetForm();
    // formik.setValues(initialValues); // Set formik values when prop changes
  };

  const addEditReferrer = async (values: any) => {
    console.log();
    try {
      setError("");
      setSuccess("");
      setLoading(true);
      console.log(formik.values);

      var _referrer: Referrer = {
        id: selectedReferrer?.id ?? "",
        name: values.name ?? "",
        contactNumber: values.contactNumber ?? "",
        email: values.email ?? "",
        isActive: values.isActive ?? false,
        code: values.code ?? "",
        nic: values.nic ?? "",
        percentage: values.percentage ?? 0,
      };

      const res = (_referrer?.id ?? "") == "" ? await CreateReferrer(_referrer) : await UpdateReferrer(_referrer);

      if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
        setSuccess("success");
        console.log(res?.data);
        setSelectedReferrer(res?.data ?? {});
        if (optParam == "n") {
          navigate(`${RoutePath.Admin_Job_create}?r=` + res?.data?.id);
        } else {
          handleResetClick();
          if (onReferrerSaveSuccess) onReferrerSaveSuccess(res?.data ?? {});
        }
      } else {
        setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
      }
      setLoading(false);
    } catch (error) {
      setError("Something went wrong...");
      setLoading(false);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
  };

  return (
    <>
      {loading && <LoadingComponent />}
      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b>
          {<>Referrer has been {!editMode ? "added" : "updated"} successfully</>}
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          <>
            Oops! Something went wrong while attempting to {!editMode ? "add" : "update"} referrer. Please try again
            later or contact support for assistance.
          </>
        </div>
      )}

      <form
        action="#!"
        id="customerForm"
        onSubmit={(event: any) => {
          event.preventDefault();
          formik.handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-x-5">
          <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-2">
            <>
              <div className="mb-4">
                <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                  Referrer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="Referrer Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.name}</p>
                ) : null}
              </div>

              <div className="mb-4">
                <label htmlFor="contactNumber" className="inline-block mb-2 text-base font-medium">
                  Contact No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contactNumber"
                  className="form-input"
                  placeholder="Referrer Contact Number"
                  value={formik.values.contactNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.contactNumber && formik.errors.contactNumber ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.contactNumber}</p>
                ) : null}
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="inline-block mb-2 text-base font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Referrer Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.email}</p>
                ) : null}
              </div>
              <div className="mb-4">
                <div>
                  <label htmlFor="isActive" className="inline-block mb-2 text-base font-medium">
                    Active
                  </label>
                  <div className="flex items-center">
                    <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                      <input
                        type="checkbox"
                        name="isActive"
                        id="isActive"
                        className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                        onChange={formik.handleChange}
                        checked={formik.values.isActive}
                      />
                      <label
                        htmlFor="isActive"
                        className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer cursor-pointertransition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                      ></label>
                    </div>
                  </div>
                </div>

                {formik.touched.isActive && formik.errors.isActive ? (
                  <p id="isActive" className="mt-1 text-sm text-red-500">
                    {formik.errors.isActive}
                  </p>
                ) : null}
              </div>

              <div className="mb-4">
                <label htmlFor="nic" className="inline-block mb-2 text-base font-medium">
                  NIC <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nic"
                  className="form-input"
                  placeholder="NIC"
                  value={formik.values.nic}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.nic && formik.errors.nic ? (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.nic}</p>
                ) : null}
              </div>
              {(selectedReferrer?.id ?? "") != "" && (
                <div className="mb-4">
                  <label htmlFor="code" className="inline-block mb-2 text-base font-medium">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    className="form-input"
                    placeholder="Referrer Code"
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={true} // Code is read-only for existing referrers
                  />
                  {formik.touched.code && formik.errors.code ? (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.code}</p>
                  ) : null}
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="percentage" className="inline-block mb-2 text-base font-bold">
                  Percentage
                </label>
                <input
                  type="text"
                  name="percentage"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Percentage"
                  value={formik.values.percentage}
                  onChange={formik.handleChange}
                  /*  onChange={(e) => {
                     if (e.target.value == "") {
                              formik.setFieldValue("additionalDiscountAmount", "0.00");
                            } else {
                              formik.setFieldValue("additionalDiscountAmount", parseFloat(e.target.value));
                            }
                            setobjTobePatched({
                              ...objTobePatched,
                              additionalDiscountAmount: parseFloat(e.target?.value ?? "0"),
                            });
                  }} */
                  // Update Formik state
                  onBlur={formik.handleBlur}
                />
                {formik.touched.percentage && formik.errors.percentage ? (
                  <p id="error_percentage" className="mt-1 text-sm text-red-500">
                    {formik.errors.percentage}
                  </p>
                ) : null}
              </div>
            </>

            <div className="mb-4"></div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={handleResetClick}
              className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
            >
              <X className="inline-block size-4" /> <span className="align-middle">Reset</span>
            </button>
            <button
              type="submit"
              className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {(selectedReferrer?.id ?? "") != "" ? "Update" : "Add"} Referrer
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddEditReferrerComponent;
